import React, { useState, useEffect } from 'react';
import { 
  Question, 
  AssessmentResponse, 
  AssessmentResult
} from '../types/assessment';
import { assessmentEngine } from '../assessment/assessment-engine';

interface AssessmentComponentProps {
  userId: string;
  onComplete?: (result: AssessmentResult) => void;
}

export const AssessmentComponent: React.FC<AssessmentComponentProps> = ({ userId, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [currentResponse, setCurrentResponse] = useState<any>(null);
  const [questionHistory, setQuestionHistory] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    // Load first question
    const nextQuestion = assessmentEngine.getNextQuestion([]);
    if (nextQuestion) {
      setCurrentQuestion(nextQuestion);
      setQuestionHistory([nextQuestion]);
    }
  }, []);

  // Load current response when question changes
  useEffect(() => {
    if (currentQuestion) {
      const existingResponse = responses.find(r => r.questionId === currentQuestion.id);
      if (existingResponse) {
        setCurrentResponse(existingResponse.value);
      } else {
        setCurrentResponse(null);
      }
    }
  }, [currentQuestion, responses]);

  const handleResponse = (value: any) => {
    if (!currentQuestion) return;

    const newResponse: AssessmentResponse = {
      questionId: currentQuestion.id,
      value,
      timestamp: new Date()
    };

    // Update or add response
    const updatedResponses = responses.filter(r => r.questionId !== currentQuestion.id);
    updatedResponses.push(newResponse);
    setResponses(updatedResponses);

    // Move to next question
    moveToNextQuestion(updatedResponses);
  };

  const moveToNextQuestion = (currentResponses: AssessmentResponse[]) => {
    const nextQuestion = assessmentEngine.getNextQuestion(currentResponses);
    
    if (nextQuestion) {
      setCurrentQuestion(nextQuestion);
      
      // Update question history
      const newHistory = [...questionHistory];
      const nextIndex = currentQuestionIndex + 1;
      
      // If moving forward to a new question, add it to history
      if (nextIndex >= newHistory.length) {
        newHistory.push(nextQuestion);
        setQuestionHistory(newHistory);
      } else {
        // If going to an existing question in history, update current question
        setCurrentQuestion(newHistory[nextIndex]);
      }
      
      setCurrentQuestionIndex(nextIndex);
      setCurrentResponse(null);
    } else {
      // Assessment complete
      const assessmentResult = assessmentEngine.processAssessment(userId, currentResponses);
      setResult(assessmentResult);
      setIsComplete(true);
      onComplete?.(assessmentResult);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const previousIndex = currentQuestionIndex - 1;
      const previousQuestion = questionHistory[previousIndex];
      
      setCurrentQuestion(previousQuestion);
      setCurrentQuestionIndex(previousIndex);
      
      // Load the previous response
      const existingResponse = responses.find(r => r.questionId === previousQuestion.id);
      if (existingResponse) {
        setCurrentResponse(existingResponse.value);
      } else {
        setCurrentResponse(null);
      }
    }
  };

  const handleNext = () => {
    if (currentQuestion?.type === 'text' || currentResponse !== null) {
      const value = currentQuestion?.type === 'text' ? 'continue' : currentResponse;
      handleResponse(value);
    }
  };

  const renderQuestionInput = (question: Question) => {
    switch (question.type) {
      case 'multiple-choice':
        return (
          <div className="space-y-3">
            {question.options?.map((option) => (
              <label key={String(option.value)} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name={question.id}
                  value={String(option.value)}
                  checked={currentResponse === option.value}
                  onChange={(e) => setCurrentResponse(option.value)}
                  className="form-radio text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        );
      
      case 'scale':
        return (
          <div className="space-y-3">
            {question.options?.map((option) => (
              <label key={String(option.value)} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name={question.id}
                  value={String(option.value)}
                  checked={currentResponse === option.value}
                  onChange={(e) => setCurrentResponse(option.value)}
                  className="form-radio text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'boolean':
        return (
          <div className="space-y-3">
            {question.options?.map((option) => (
              <label key={String(option.value)} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name={question.id}
                  value={String(option.value)}
                  checked={currentResponse === option.value}
                  onChange={(e) => setCurrentResponse(option.value)}
                  className="form-radio text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'multi-select':
        return (
          <div className="space-y-3">
            {question.options?.map((option) => (
              <label key={String(option.value)} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  value={String(option.value)}
                  checked={Array.isArray(currentResponse) && currentResponse.includes(option.value)}
                  onChange={(e) => {
                    const currentValues = Array.isArray(currentResponse) ? currentResponse : [];
                    if (e.target.checked) {
                      setCurrentResponse([...currentValues, option.value]);
                    } else {
                      setCurrentResponse(currentValues.filter((v: any) => v !== option.value));
                    }
                  }}
                  className="form-checkbox text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'text':
        return (
          <div className="text-gray-600 italic bg-blue-50 p-4 rounded-lg">
            {question.text}
          </div>
        );

      default:
        return null;
    }
  };

  const renderResults = (result: AssessmentResult) => {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-6">Your Mental Health Assessment Results</h2>
        
        {/* Risk Flags */}
        {result.riskFlags.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-red-600">Important Alerts</h3>
            {result.riskFlags.map((flag, index) => (
              <div key={index} className={`p-4 rounded-lg mb-4 ${
                flag.severity === 'critical' ? 'bg-red-100 border-red-400' :
                flag.severity === 'high' ? 'bg-orange-100 border-orange-400' :
                'bg-yellow-100 border-yellow-400'
              } border-l-4`}>
                <h4 className="font-semibold">{flag.description}</h4>
                {flag.immediateAction && (
                  <p className="mt-2 text-sm">{flag.immediateAction}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Category Scores */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Assessment Scores</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {result.scores.map((score, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold capitalize">{score.category}</h4>
                <div className="mt-2">
                  <div className="flex justify-between text-sm">
                    <span>Score: {score.score}/{score.maxScore}</span>
                    <span className={`font-semibold ${
                      score.severity === 'low' ? 'text-green-600' :
                      score.severity === 'mild' ? 'text-yellow-600' :
                      score.severity === 'moderate' ? 'text-orange-600' :
                      'text-red-600'
                    }`}>
                      {score.severity.toUpperCase()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      className={`h-2 rounded-full ${
                        score.severity === 'low' ? 'bg-green-500' :
                        score.severity === 'mild' ? 'bg-yellow-500' :
                        score.severity === 'moderate' ? 'bg-orange-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${score.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Personalized Recommendations</h3>
          {result.recommendations.map((rec, index) => (
            <div key={index} className="p-4 border rounded-lg mb-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold">{rec.title}</h4>
                <span className={`px-2 py-1 rounded text-xs ${
                  rec.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                  rec.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                  rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {rec.priority.toUpperCase()}
                </span>
              </div>
              <p className="text-gray-600 mb-3">{rec.description}</p>
              
              {rec.resources && rec.resources.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2">Resources:</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {rec.resources.map((resource, resourceIndex) => (
                      <li key={resourceIndex} className="text-sm">
                        {resource.url ? (
                          <a href={resource.url} target="_blank" rel="noopener noreferrer" 
                             className="text-blue-600 hover:underline">
                            {resource.title}
                          </a>
                        ) : (
                          <span className="font-medium">{resource.title}</span>
                        )}
                        {resource.description && (
                          <span className="text-gray-500"> - {resource.description}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-sm text-gray-500 border-t pt-4">
          <p>Assessment completed on {result.completedAt.toLocaleDateString()}</p>
          <p>Version: {result.version}</p>
          <p className="mt-2">
            <strong>Disclaimer:</strong> This assessment is for informational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of qualified health providers with any questions you may have regarding your mental health.
          </p>
        </div>
      </div>
    );
  };

  if (isComplete && result) {
    return renderResults(result);
  }

  if (!currentQuestion) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4">Loading assessment...</p>
        </div>
      </div>
    );
  }

  const progress = (currentQuestionIndex / assessmentEngine.getAllQuestions().length) * 100;

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-white rounded-lg shadow-lg min-h-[500px]">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress</span>
          <span>{Math.min(currentQuestionIndex + 1, assessmentEngine.getAllQuestions().length)} of {assessmentEngine.getAllQuestions().length} questions</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(progress, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* Category indicator */}
      {currentQuestion?.category && (
        <div className="mb-4">
          <span className="inline-block px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
            {currentQuestion.category.replace('-', ' ').toUpperCase()}
          </span>
        </div>
      )}

      {/* Question */}
      <div className="mb-8">
        <h2 className="text-lg sm:text-xl font-semibold mb-6 text-gray-800 leading-relaxed">
          {currentQuestion.text}
        </h2>
        <div className="space-y-2">
          {renderQuestionInput(currentQuestion)}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <button
          onClick={handlePrevious}
          className="px-4 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          disabled={currentQuestionIndex === 0}
        >
          ← Previous
        </button>
        
        <button
          onClick={handleNext}
          disabled={currentQuestion?.type !== 'text' && (currentResponse === null || currentResponse === undefined || 
            (currentQuestion?.type === 'multi-select' && Array.isArray(currentResponse) && currentResponse.length === 0))}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-sm"
        >
          {currentQuestion?.type === 'text' ? 'Continue' : 'Next'} →
        </button>
      </div>
    </div>
  );
};

export default AssessmentComponent; 